import type {
  TileBrowseDecoration,
  TileBrowseTaxonomyNode,
  TileCatalogPayload,
} from "@west-marches/shared";
import { getPaintSetIdForDecoration } from "@west-marches/shared";

export interface TileCatalogBrowserSection {
  kind: "collection" | "taxonomy";
  id: string;
  label: string;
  sortOrder: number;
  tileSetIds: string[];
  /**
   * Underlying taxonomy node kind. Only set for `kind: "taxonomy"` sections;
   * controls whether the thumbnail resolver looks at `tileSetIds` (tiles)
   * or `decorationIds` (overlays). Defaults to `"tile"` when omitted.
   */
  nodeKind?: "tile" | "overlay";
  /** Direct decoration ids on the underlying taxonomy node, used for overlay folder thumbnails. */
  decorationIds?: string[];
}

export interface TileCatalogTaxonomyTreeNode {
  section: TileCatalogBrowserSection;
  visibleSubtreeTileSetIds: string[];
  children: TileCatalogTaxonomyTreeNode[];
}

export type TileCatalogBrowserDetailEntry =
  | {
      kind: "section";
      section: TileCatalogBrowserSection;
      visibleTileSetIds: string[];
      countTileSetIds: string[];
    }
  | {
      kind: "tile-set";
      tileSetId: string;
      sortOrder: number;
    }
  | {
      kind: "decoration";
      decorationId: string;
      sortOrder: number;
    };

function compareBySortOrder(
  left: { sortOrder: number; label: string; kind: string; id: string },
  right: { sortOrder: number; label: string; kind: string; id: string },
): number {
  if (left.sortOrder !== right.sortOrder) {
    return left.sortOrder - right.sortOrder;
  }
  const labelCompare = left.label.localeCompare(right.label);
  if (labelCompare !== 0) {
    return labelCompare;
  }
  if (left.kind !== right.kind) {
    return left.kind.localeCompare(right.kind);
  }
  return left.id.localeCompare(right.id);
}

export interface TileCatalogSectionGroup {
  title: string;
  items: TileCatalogBrowserSection[];
}

export type TileCatalogBrowserRootEntry =
  | {
      kind: "section";
      section: TileCatalogBrowserSection;
      visibleTileSetIds: string[];
    }
  | {
      kind: "tile-set";
      tileSetId: string;
      sortOrder: number;
    };

/** Union of tile set ids on this node and all descendant taxonomy nodes (for root thumbnails / counts). */
export function collectTaxonomySubtreeTileSetIds(
  taxonomy: TileBrowseTaxonomyNode[],
  rootId: string,
): string[] {
  const byParent = new Map<string | null, TileBrowseTaxonomyNode[]>();
  for (const node of taxonomy) {
    const list = byParent.get(node.parentId) ?? [];
    list.push(node);
    byParent.set(node.parentId, list);
  }
  const ids = new Set<string>();
  const stack = [rootId];
  while (stack.length > 0) {
    const id = stack.pop();
    if (!id) {
      continue;
    }
    const node = taxonomy.find((n) => n.id === id);
    if (!node) {
      continue;
    }
    for (const tileSetId of node.tileSetIds) {
      ids.add(tileSetId);
    }
    for (const child of byParent.get(id) ?? []) {
      stack.push(child.id);
    }
  }
  return [...ids];
}

/** Union of decoration ids attached to this node and all of its descendants. */
export function collectTaxonomySubtreeDecorationIds(
  taxonomy: TileBrowseTaxonomyNode[],
  rootId: string,
): string[] {
  const byParent = new Map<string | null, TileBrowseTaxonomyNode[]>();
  for (const node of taxonomy) {
    const list = byParent.get(node.parentId) ?? [];
    list.push(node);
    byParent.set(node.parentId, list);
  }
  const ids = new Set<string>();
  const stack = [rootId];
  while (stack.length > 0) {
    const id = stack.pop();
    if (!id) {
      continue;
    }
    const node = taxonomy.find((n) => n.id === id);
    if (!node) {
      continue;
    }
    for (const decorationId of node.decorationIds) {
      ids.add(decorationId);
    }
    for (const child of byParent.get(id) ?? []) {
      stack.push(child.id);
    }
  }
  return [...ids];
}

function isNodeVisibleForKind(
  payload: TileCatalogPayload,
  node: TileBrowseTaxonomyNode,
  hiddenTileSetIdSet: Set<string>,
  kindFilter: "tile" | "overlay",
): boolean {
  // Default missing `kind` to "tile" so legacy fixtures and any older
  // payloads continue to render under the Tiles tab.
  const nodeKind = node.kind ?? "tile";
  if (nodeKind !== kindFilter) {
    return false;
  }
  if (kindFilter === "overlay") {
    return collectTaxonomySubtreeDecorationIds(payload.browse.taxonomy, node.id).length > 0;
  }
  return taxonomySubtreeVisibleTileSetIds(payload, node.id, hiddenTileSetIdSet).length > 0;
}

export function getChildTaxonomySections(
  payload: TileCatalogPayload,
  parentId: string,
  hiddenTileSetIdSet: Set<string>,
  kindFilter: "tile" | "overlay" = "tile",
): TileCatalogBrowserSection[] {
  return payload.browse.taxonomy
    .filter((node) => node.parentId === parentId)
    .filter((node) => isNodeVisibleForKind(payload, node, hiddenTileSetIdSet, kindFilter))
    .map((node) => ({
      kind: "taxonomy" as const,
      id: node.id,
      label: node.label,
      sortOrder: node.sortOrder,
      tileSetIds: node.tileSetIds,
      nodeKind: node.kind ?? "tile",
      decorationIds: node.decorationIds,
    }))
    .sort((left, right) =>
      compareBySortOrder(
        { sortOrder: left.sortOrder, label: left.label, kind: left.kind, id: left.id },
        { sortOrder: right.sortOrder, label: right.label, kind: right.kind, id: right.id },
      ),
    );
}

export function getRootTaxonomySections(
  payload: TileCatalogPayload,
  hiddenTileSetIdSet: Set<string>,
  kindFilter: "tile" | "overlay" = "tile",
): TileCatalogBrowserSection[] {
  return payload.browse.taxonomy
    .filter((node) => node.parentId === null)
    .filter((node) => isNodeVisibleForKind(payload, node, hiddenTileSetIdSet, kindFilter))
    .map((node) => ({
      kind: "taxonomy" as const,
      id: node.id,
      label: node.label,
      sortOrder: node.sortOrder,
      tileSetIds: node.tileSetIds.filter((tileSetId) => !hiddenTileSetIdSet.has(tileSetId)),
      nodeKind: node.kind ?? "tile",
      decorationIds: node.decorationIds,
    }))
    .sort((left, right) =>
      compareBySortOrder(
        { sortOrder: left.sortOrder, label: left.label, kind: left.kind, id: left.id },
        { sortOrder: right.sortOrder, label: right.label, kind: right.kind, id: right.id },
      ),
    );
}

export function taxonomySubtreeVisibleTileSetIds(
  payload: TileCatalogPayload,
  taxonomyNodeId: string,
  hiddenTileSetIdSet: Set<string>,
): string[] {
  return collectTaxonomySubtreeTileSetIds(payload.browse.taxonomy, taxonomyNodeId).filter(
    (id) => !hiddenTileSetIdSet.has(id),
  );
}

export function getTileCatalogBrowserSectionGroups(
  payload: TileCatalogPayload,
  kindFilter: "tile" | "overlay" = "tile",
): TileCatalogSectionGroup[] {
  // Collections only apply to tiles; the decoration tree skips this group.
  const featuredCollections =
    kindFilter === "tile"
      ? payload.browse.collections
          .filter((collection) => collection.isFeatured && collection.tileSetIds.length > 0)
          .map((collection, index) => ({
            kind: "collection" as const,
            id: collection.id,
            label: collection.title,
            sortOrder: index,
            tileSetIds: collection.tileSetIds,
          }))
      : [];

  const taxonomyNodes = payload.browse.taxonomy
    .filter((node) => node.parentId === null && (node.kind ?? "tile") === kindFilter)
    .filter((node) => {
      if (kindFilter === "overlay") {
        return collectTaxonomySubtreeDecorationIds(payload.browse.taxonomy, node.id).length > 0;
      }
      return collectTaxonomySubtreeTileSetIds(payload.browse.taxonomy, node.id).length > 0;
    })
    .map((node) => ({
      kind: "taxonomy" as const,
      id: node.id,
      label: node.label,
      sortOrder: node.sortOrder,
      tileSetIds: node.tileSetIds,
      nodeKind: node.kind ?? "tile",
      decorationIds: node.decorationIds,
    }));

  const groups: TileCatalogSectionGroup[] = [];
  if (featuredCollections.length > 0) {
    groups.push({ title: "Featured Collections", items: featuredCollections });
  }
  if (taxonomyNodes.length > 0) {
    groups.push({ title: "", items: taxonomyNodes });
  }

  return groups;
}

export function buildTaxonomyTree(
  payload: TileCatalogPayload,
  hiddenTileSetIdSet: Set<string>,
): TileCatalogTaxonomyTreeNode[] {
  const byParent = new Map<string | null, TileBrowseTaxonomyNode[]>();
  for (const node of payload.browse.taxonomy) {
    const siblings = byParent.get(node.parentId) ?? [];
    siblings.push(node);
    byParent.set(node.parentId, siblings);
  }

  const sortNodes = (nodes: TileBrowseTaxonomyNode[]) =>
    [...nodes].sort((left, right) =>
      compareBySortOrder(
        { sortOrder: left.sortOrder, label: left.label, kind: "taxonomy", id: left.id },
        { sortOrder: right.sortOrder, label: right.label, kind: "taxonomy", id: right.id },
      ),
    );

  function buildNode(node: TileBrowseTaxonomyNode): TileCatalogTaxonomyTreeNode | null {
    const children = sortNodes(byParent.get(node.id) ?? [])
      .map((child) => buildNode(child))
      .filter((child): child is TileCatalogTaxonomyTreeNode => child !== null);
    const directVisibleTileSetIds = node.tileSetIds.filter((tileSetId) => !hiddenTileSetIdSet.has(tileSetId));
    const subtreeVisibleTileSetIds = [...new Set([
      ...directVisibleTileSetIds,
      ...children.flatMap((child) => child.visibleSubtreeTileSetIds),
    ])];
    if (subtreeVisibleTileSetIds.length === 0) {
      return null;
    }

    return {
      section: {
        kind: "taxonomy",
        id: node.id,
        label: node.label,
        sortOrder: node.sortOrder,
        tileSetIds: directVisibleTileSetIds,
        nodeKind: node.kind ?? "tile",
        decorationIds: node.decorationIds,
      },
      visibleSubtreeTileSetIds: subtreeVisibleTileSetIds,
      children,
    };
  }

  return sortNodes(byParent.get(null) ?? [])
    .map((node) => buildNode(node))
    .filter((node): node is TileCatalogTaxonomyTreeNode => node !== null);
}

export function buildTaxonomySectionPath(
  payload: TileCatalogPayload,
  taxonomyNodeId: string,
  hiddenTileSetIdSet: Set<string>,
): TileCatalogBrowserSection[] {
  const visibleTree = buildTaxonomyTree(payload, hiddenTileSetIdSet);

  function findPath(
    nodes: TileCatalogTaxonomyTreeNode[],
    currentPath: TileCatalogBrowserSection[],
  ): TileCatalogBrowserSection[] | null {
    for (const node of nodes) {
      const nextPath = [...currentPath, node.section];
      if (node.section.id === taxonomyNodeId) {
        return nextPath;
      }
      const childPath = findPath(node.children, nextPath);
      if (childPath) {
        return childPath;
      }
    }
    return null;
  }

  return findPath(visibleTree, []) ?? [];
}

export function getRootTileSetEntries(
  payload: TileCatalogPayload,
  hiddenTileSetIdSet: Set<string>,
): Array<{ tileSetId: string; sortOrder: number }> {
  return (payload.browse.rootTileSetEntries ?? []).filter(
    (entry) => !hiddenTileSetIdSet.has(entry.tileSetId),
  );
}

export function getRootDetailEntries(
  payload: TileCatalogPayload,
  sectionGroups: TileCatalogSectionGroup[],
  hiddenTileSetIdSet: Set<string>,
  kindFilter: "tile" | "overlay" = "tile",
): TileCatalogBrowserRootEntry[] {
  const tileSetLabelById = new Map(
    payload.manifest.tileSets.map((tileSet) => [tileSet.id, tileSet.label] as const),
  );
  const sortableEntries: Array<{
    entry: TileCatalogBrowserRootEntry;
    sortOrder: number;
    label: string;
    kindLabel: string;
    id: string;
  }> = [];

  for (const group of sectionGroups) {
    for (const section of group.items) {
      if (section.kind === "collection") {
        const visibleTileSetIds = section.tileSetIds.filter(
          (tileSetId) => !hiddenTileSetIdSet.has(tileSetId),
        );
        if (visibleTileSetIds.length > 0) {
          sortableEntries.push({
            entry: { kind: "section", section, visibleTileSetIds },
            sortOrder: section.sortOrder,
            label: section.label,
            kindLabel: section.kind,
            id: section.id,
          });
        }
        continue;
      }

      const countTileSetIds =
        kindFilter === "overlay"
          ? collectTaxonomySubtreeDecorationIds(payload.browse.taxonomy, section.id)
          : taxonomySubtreeVisibleTileSetIds(payload, section.id, hiddenTileSetIdSet);
      if (countTileSetIds.length === 0) {
        continue;
      }

      sortableEntries.push({
        entry: {
          kind: "section",
          section,
          visibleTileSetIds: section.tileSetIds.filter(
            (tileSetId) => !hiddenTileSetIdSet.has(tileSetId),
          ),
        },
        sortOrder: section.sortOrder,
        label: section.label,
        kindLabel: section.kind,
        id: section.id,
      });
    }
  }

  if (kindFilter === "tile") {
    sortableEntries.push(
      ...getRootTileSetEntries(payload, hiddenTileSetIdSet).map((entry) => ({
        entry: { kind: "tile-set" as const, tileSetId: entry.tileSetId, sortOrder: entry.sortOrder },
        sortOrder: entry.sortOrder,
        label: tileSetLabelById.get(entry.tileSetId) ?? entry.tileSetId,
        kindLabel: "tile-set",
        id: entry.tileSetId,
      })),
    );
  }

  return sortableEntries
    .sort((left, right) =>
      compareBySortOrder(
        { sortOrder: left.sortOrder, label: left.label, kind: left.kindLabel, id: left.id },
        { sortOrder: right.sortOrder, label: right.label, kind: right.kindLabel, id: right.id },
      ),
    )
    .map(({ entry }) => entry);
}

export function getTaxonomyDetailEntries(
  payload: TileCatalogPayload,
  taxonomyNodeId: string,
  hiddenTileSetIdSet: Set<string>,
  kindFilter: "tile" | "overlay" = "tile",
): TileCatalogBrowserDetailEntry[] {
  const tileSetLabelById = new Map(
    payload.manifest.tileSets.map((tileSet) => [tileSet.id, tileSet.label] as const),
  );
  const childSections = getChildTaxonomySections(
    payload,
    taxonomyNodeId,
    hiddenTileSetIdSet,
    kindFilter,
  );
  const taxonomyNode = payload.browse.taxonomy.find((node) => node.id === taxonomyNodeId);
  const tileSetEntries =
    kindFilter === "tile"
      ? (taxonomyNode?.tileSetEntries ?? []).filter(
          (entry) => !hiddenTileSetIdSet.has(entry.tileSetId),
        )
      : [];

  const sortableEntries = [
    ...childSections.map((section) => ({
      entry: {
        kind: "section" as const,
        section,
        visibleTileSetIds: section.tileSetIds.filter((tileSetId) => !hiddenTileSetIdSet.has(tileSetId)),
        countTileSetIds:
          kindFilter === "overlay"
            ? collectTaxonomySubtreeDecorationIds(payload.browse.taxonomy, section.id)
            : taxonomySubtreeVisibleTileSetIds(payload, section.id, hiddenTileSetIdSet),
      },
      sortOrder: section.sortOrder,
      label: section.label,
      kindLabel: section.kind,
      id: section.id,
    })),
    ...tileSetEntries.map((entry) => ({
      entry: {
        kind: "tile-set" as const,
        tileSetId: entry.tileSetId,
        sortOrder: entry.sortOrder,
      },
      sortOrder: entry.sortOrder,
      label: tileSetLabelById.get(entry.tileSetId) ?? entry.tileSetId,
      kindLabel: "tile-set",
      id: entry.tileSetId,
    })),
  ];

  return sortableEntries
    .sort((left, right) =>
      compareBySortOrder(
        { sortOrder: left.sortOrder, label: left.label, kind: left.kindLabel, id: left.id },
        { sortOrder: right.sortOrder, label: right.label, kind: right.kindLabel, id: right.id },
      ),
    )
    .map(({ entry }) => entry);
}

export function getTaxonomyDecorationEntries(
  payload: TileCatalogPayload,
  taxonomyNodeId: string,
): TileBrowseDecoration[] {
  const taxonomyNode = payload.browse.taxonomy.find((node) => node.id === taxonomyNodeId);
  if (!taxonomyNode) {
    return [];
  }

  const decorationById = new Map(
    payload.browse.decorations.map((decoration) => [decoration.id, decoration] as const),
  );

  const seenPaintSetIds = new Set<string>();

  return taxonomyNode.decorationEntries
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .flatMap((entry) => {
      const decoration = decorationById.get(entry.decorationId);
      if (!decoration) {
        return [];
      }
      const paintSetId = decoration.paintSetId ?? getPaintSetIdForDecoration(decoration.id);
      if (paintSetId) {
        if (seenPaintSetIds.has(paintSetId)) {
          return [];
        }
        seenPaintSetIds.add(paintSetId);
      }
      return [decoration];
    });
}

export function getDecorationBrowseSequence(
  payload: TileCatalogPayload,
  selectedDecorationId: string | null,
  preferredTaxonomyNodeId: string | null,
): TileBrowseDecoration[] {
  if (!selectedDecorationId) {
    return [];
  }

  const decoration = payload.browse.decorations.find(
    (entry) => entry.id === selectedDecorationId,
  );
  if (!decoration) {
    return [];
  }

  const candidateNodeIds = [
    preferredTaxonomyNodeId,
    ...decoration.taxonomyNodeIds,
    ...payload.browse.taxonomy
      .filter((node) =>
        node.decorationEntries.some((entry) => entry.decorationId === selectedDecorationId),
      )
      .map((node) => node.id),
  ].filter((nodeId, index, values): nodeId is string => Boolean(nodeId) && values.indexOf(nodeId) === index);

  for (const nodeId of candidateNodeIds) {
    const orderedDecorations = getTaxonomyDecorationEntries(payload, nodeId);
    if (
      orderedDecorations.length > 1 &&
      orderedDecorations.some((entry) => entry.id === selectedDecorationId)
    ) {
      return orderedDecorations;
    }
  }

  const taxonomyNodeById = new Map(payload.browse.taxonomy.map((node) => [node.id, node] as const));
  const ancestorCandidateNodeIds: string[] = [];
  for (const nodeId of candidateNodeIds) {
    let currentNodeId = taxonomyNodeById.get(nodeId)?.parentId ?? null;
    while (currentNodeId) {
      if (
        !candidateNodeIds.includes(currentNodeId) &&
        !ancestorCandidateNodeIds.includes(currentNodeId)
      ) {
        ancestorCandidateNodeIds.push(currentNodeId);
      }
      currentNodeId = taxonomyNodeById.get(currentNodeId)?.parentId ?? null;
    }
  }

  for (const nodeId of ancestorCandidateNodeIds) {
    const orderedDecorations = getTaxonomyDecorationEntries(payload, nodeId);
    if (
      orderedDecorations.length > 1 &&
      orderedDecorations.some((entry) => entry.id === selectedDecorationId)
    ) {
      return orderedDecorations;
    }
  }

  for (const nodeId of candidateNodeIds) {
    const orderedDecorations = getTaxonomyDecorationEntries(payload, nodeId);
    if (orderedDecorations.some((entry) => entry.id === selectedDecorationId)) {
      return orderedDecorations;
    }
  }

  return [decoration];
}

/** First `maxSlots` visible sections in the same order as `TileCatalogBrowser` renders them (for 1–9 hotkeys). */
export function buildRootHotkeySlots(
  payload: TileCatalogPayload,
  sectionGroups: TileCatalogSectionGroup[],
  hiddenTileSetIdSet: Set<string>,
  maxSlots = 9,
  kindFilter: "tile" | "overlay" = "tile",
): TileCatalogBrowserRootEntry[] {
  return getRootDetailEntries(payload, sectionGroups, hiddenTileSetIdSet, kindFilter).slice(
    0,
    maxSlots,
  );
}
